import { useForm } from 'antd/es/form/Form';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { get } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { format } from 'url';

import { ExplorerContext } from '@page/explorer/explorer.provider';
import { paramsReplacer, stringReplacer } from '@page/explorer/explorer.util';
import { ExplorerDataInterface } from '@page/explorer/interface/explorer-data.interface';
import { PostmanItemURLInterface } from '@page/explorer/interface/postman-collection.interface';

import { isExist, isString } from '@util/data/is-exist';

export const useExplorer = (mapData: Map<string, ExplorerDataInterface>) => {
  const { variables, currentEnv, setVariables } = useContext(ExplorerContext);
  const [form] = useForm();
  const [currentApiKey, setKey] = useState('');
  const [response, setResponse] = useState<AxiosResponse | AxiosError>({} as AxiosResponse);
  const [isLoading, setLoading] = useState(false);

  const handleResponse = (res: AxiosResponse | AxiosError) => {
    setLoading(false);
    setResponse(res);
  };

  useEffect(() => {
    if (!isString(currentApiKey)) {
      let newKey = '';
      mapData.forEach((item) => {
        if (!isString(newKey) && isExist(item.request)) {
          newKey = item.id;
        }
      });

      if (newKey) {
        setKey(newKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData]);

  useEffect(() => {
    setResponse({} as AxiosResponse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApiKey, mapData]);

  const onCall = (formData: Record<string, Record<string, string>>) => {
    const schema = mapData.get(currentApiKey);

    if (!isExist(schema)) return;

    const url = get(schema, ['request', 'url'], {} as PostmanItemURLInterface);

    const host = stringReplacer(url.host.at(0) ?? '', variables, currentEnv);
    const link = format({
      host,
      pathname: url.path
        .map((path) => paramsReplacer(stringReplacer(path, variables, currentEnv), formData.params, currentEnv))
        .join('/'),
    }).replace(/^\/\//, '');

    const params =
      isExist(formData.query) &&
      Object.fromEntries(Object.entries(formData.query).filter(([, value]) => isString(value)));
    const data =
      isExist(formData.body) &&
      Object.fromEntries(Object.entries(formData.body).filter(([, value]) => isString(value)));

    setLoading(true);

    axios({
      url: link,
      method: schema.request.method,
      ...(params && { params }),
      ...(data && { data }),
      ...(isExist(formData.headers) && { headers: formData.headers }),
    })
      .then((res) => handleResponse(res))
      .catch(handleResponse);
  };

  useEffect(() => {
    const data = get(response, ['data', 'data'], {});
    const schema = (get(mapData.get(currentApiKey), ['event', 0, 'script', 'exec'], []).at(-1) ?? '{}').replace(
      '//',
      '',
    );

    try {
      const responseKeys = JSON.parse(isString(schema) ? schema : '{}') as Record<string, string>;

      setVariables(
        Object.entries(responseKeys).reduce((acc, [key, value]) => {
          if (!isExist(data[key])) return acc;

          return {
            ...acc,
            [value]: data[key],
          };
        }, {}),
      );
    } catch (error) {
      console.log('error', error);
    }
  }, [response, currentApiKey]);

  return {
    form,
    currentApiKey,
    setKey,
    onCall,
    response,
    isLoading,
  };
};
